import { useMemo } from 'react';
import DocumentTitle from 'react-document-title';
import { useParams, useNavigate } from 'react-router-dom';

import { Loader } from '@hivebrite/design-system/Loader';
import { DefaultAppContexts } from '@hivebrite/frontoffice-default-app-contexts';
import { NewsShowView } from '@hivebrite/frontoffice-news';

import NewsNotFound from 'hb-react/frontoffice/apps/NewsIndex/components/NewsNotFound';
import { useTopicContext } from 'hb-react/frontoffice/contexts/TopicContext';
import { useNewsPublished } from 'hb-react/shared/apps/News/NewsPublished/hooks/useNewsPublished';
import NewsReactions from 'hb-react/shared/apps/News/NewsPublished/NewsReactions';
import { useQueryRelatedNews } from 'hb-react/shared/apps/News/NewsPublished/NewsRelated/hooks/useQueryRelatedNews';
import { ContentRecommendation } from 'hb-react/shared/components/ContentRecommendation';
import PublicationLayout from 'hb-react/shared/components/Publication/PublicationLayout';
import { useCurrentNetwork } from 'hb-react/shared/contexts';
import { useFrontMonorepoCurrentNetwork as useFrontMonorepoDefaultAppContexts } from 'hb-react/shared/hooks/frontMonorepo/useFrontMonorepoDefaultAppContexts';
import useFeatureFlag from 'hb-react/shared/hooks/useFeatureFlag';
import { usePageMenuName, PAGES } from 'hb-react/shared/hooks/usePageMenuName';
import { ENTITY_TYPES } from 'hb-react/shared/utils/entitiesContentData/constants';
import { isCommentsModuleAvailable } from 'hb-react/shared/utils/features/comments';
import { generatePageTitle } from 'hb-react/shared/utils/generatePageTitle';

const configs = {};
const handlers = {};

export const NewsPackage = ({
  config,
  withDocumentTitle = true,
}: {
  config: any;
  withDocumentTitle?: boolean;
}) => {
  const { newsId } = useParams();
  const navigate = useNavigate();
  const { currentNetwork } = useCurrentNetwork();
  const { currentNetwork: frontMonorepoCurrentNetwork, currentUser } =
    useFrontMonorepoDefaultAppContexts();
  const { flagUjAiContentRecommendation, flagActivateFrontMonitoringDatadog } =
    useFeatureFlag();
  const { topicId, whitelabelURL, topicName, whitelabelName } =
    useTopicContext();

  const flags = useMemo(
    () => ({
      'flag-activate-front-monitoring-datadog':
        flagActivateFrontMonitoringDatadog,
    }),
    [flagActivateFrontMonitoringDatadog],
  );

  // Only display related news in the PO
  const showRelatedNews = !currentUser;

  const { news, isLoading, error } = useNewsPublished(newsId, currentUser?.id);
  const { data: relatedNews } = useQueryRelatedNews({
    enabled: showRelatedNews,
    userId: currentUser?.id,
    topicId,
    whiteLabelTopic: !!whitelabelURL,
    newsId,
  });
  const data = useMemo(() => ({ news, relatedNews }), [news, relatedNews]);

  const configPageName = config?.custom?.page?.menu_name;
  const appPagesMenuName = usePageMenuName(PAGES.NEWS, __('News'), {
    enabled: !configPageName,
  });
  const menuName = configPageName || appPagesMenuName;

  if (isLoading || !frontMonorepoCurrentNetwork) {
    return <Loader size={50} />;
  }

  if (error || !news) {
    return (
      <PublicationLayout>
        <NewsNotFound />
      </PublicationLayout>
    );
  }

  const isContentRecommendationVisible =
    flagUjAiContentRecommendation &&
    currentUser &&
    currentNetwork?.network_configuration?.ai_content_recommendation?.enabled;

  return (
    <DefaultAppContexts
      locale={window.__HB_LOCALE__}
      // @ts-expect-error type mismatch with lat
      currentUser={currentUser}
      currentNetwork={frontMonorepoCurrentNetwork}
      currentTopic={{
        id: topicId,
        name: topicName,
        whitelabelActivated: !!whitelabelURL,
        whitelabelName: whitelabelName,
      }}
      navigateTo={navigate}
    >
      {withDocumentTitle && (
        <DocumentTitle
          title={generatePageTitle({
            title: news?.subject || menuName || '',
            networkName: currentNetwork?.page_title || currentNetwork?.title,
            topicName: topicName,
          })}
        />
      )}
      <NewsShowView
        config={configs}
        // @ts-expect-error TODO Check with Lucas L
        data={data}
        flags={flags}
        handlers={handlers}
      >
        {isContentRecommendationVisible && (
          <ContentRecommendation
            contentId={parseInt(news.id, 10)}
            contentType={ENTITY_TYPES.news}
          />
        )}
        {isCommentsModuleAvailable(
          currentUser,
          !currentNetwork?.network_configuration?.news?.comments_enabled,
        ) && <NewsReactions id={news.id} likes={news.likes} />}
      </NewsShowView>
    </DefaultAppContexts>
  );
};
